/**
 * Measurements (F4, F5): measure across, wall length, conflicts.
 * Each function mutates the plan in place only when it returns ok.
 */
import { MANUAL_SOURCE, POLICY, SAME_TOL_FT } from './policy';
import { EPS, axisOf, dist, edgeOf, findRoom, tidy } from './geom';
import { buildUF, compOf, edgeKnown, knownCount } from './uf';
import { isPinned, sharedMap } from './shared';
import { findOpposite } from './opposite';
import { heldChoices, heldSides, moveHeldSide, splitSide, type HeldChoice } from './heldMove';
import type { Axis, Fail, Measurement, Plan, Room } from './types';

export type DistanceResult =
  | { ok: true; moved: 'p' | 'q' }
  | { ok: false; err: 'same'; same: true; message: string }
  | { ok: false; err: 'conflict'; conflict: true; cur: number; off: number; message: string }
  /** K15 case 2: both walls are held by attached rooms and neither is a split line: the user picks. */
  | { ok: false; err: 'choose-wall'; options: HeldChoice[]; message: string }
  /** The room across the wall that has to move refuses (its size is measured, or it would be too small). */
  | { ok: false; err: 'measured' | 'too-small'; rid: string; message: string }
  | { ok: false; err: 'missing'; message: string };

/**
 * Set the distance between the coordinate groups of corners p and q on axis
 * ax. Moves one whole group so earlier measurements stay true. `moveQ` asks to
 * move q's group; a group held by a shared wall does not move when the other is
 * free (F5). Same-group readings return `same` (within the policy tolerance) or
 * `conflict` with the current value and the difference.
 *
 * PRP-SKETCH-FIELD-4: a value that needs no move records the measurement whatever
 * holds the walls (K10). When both groups are held (K15): the side across the split
 * line moves (its room follows, DD-SF4.1: `moveQ` does not override it); otherwise
 * `choose-wall` lists both walls, and `chosen: true` moves the `moveQ` side even
 * though it is held, the room across following by the same amount (J17).
 */
export function trySetDistance(
  plan: Plan, r: Room, ax: Axis, p: number, q: number, val: number, moveQ: boolean, kind: Measurement['kind'],
  opts: { chosen?: boolean } = {},
): DistanceResult {
  const vp = r.corners.find((v) => v.id === p), vq = r.corners.find((v) => v.id === q);
  if (!vp || !vq) return { ok: false, err: 'missing', message: 'That wall is gone' };
  const U = buildUF(r);
  const cur = vq[ax] - vp[ax];
  if (U.same(ax, p, q)) {
    const off = Math.abs(cur) - val;
    if (Math.abs(off) < SAME_TOL_FT) return { ok: false, err: 'same', same: true, message: 'Matches the earlier measurements' };
    return { ok: false, err: 'conflict', conflict: true, cur: Math.abs(cur), off, message: "Reading doesn't match" };
  }
  const sign = cur < 0 ? -1 : 1, d = sign * val - cur;
  const record = (moved: 'p' | 'q'): DistanceResult => {
    r.measurements.push({ axis: ax, a: p, b: q, valueFt: val, kind, source: MANUAL_SOURCE });
    // Measuring the room again clears its check-dimensions note (FIELD-2 E4).
    delete r.checkDims;
    tidy(plan);
    return { ok: true, moved };
  };
  // K10: the reading matches the drawing, so nothing moves and nothing can be held.
  if (Math.abs(d) < EPS) return record(moveQ ? 'q' : 'p');
  const M = sharedMap(plan);
  const pinP = isPinned(plan, r, U, ax, p, M), pinQ = isPinned(plan, r, U, ax, q, M);
  let mq = moveQ;
  if (!opts.chosen) {
    if (pinP && pinQ) {
      const sides = heldSides(plan, r, U, ax, p, q, M);
      const pick = splitSide(sides);
      if (!pick) {
        return {
          ok: false, err: 'choose-wall', options: heldChoices(sides),
          message: 'Both walls are shared with other rooms. Pick the wall that moves; the room across it changes by the same amount.',
        };
      }
      mq = pick.moveQ;
    } else if (mq && pinQ) mq = false;
    else if (!mq && pinP) mq = true;
  }
  const delta = mq ? d : -d;
  if (mq ? pinQ : pinP) {
    const res = moveHeldSide(plan, r, U, ax, mq ? q : p, delta, M);
    if (!res.ok) return res;
  } else compOf(r, U, ax, mq ? q : p).forEach((v) => { v[ax] += delta; });
  return record(mq ? 'q' : 'p');
}

/** Drop every measurement in vid's group on axis ax (the "use new" path). */
export function clearAxisGroup(r: Room, ax: Axis, vid: number): void {
  const U = buildUF(r);
  const root = U.find(ax, vid);
  r.measurements = r.measurements.filter((m) => !(m.axis === ax && U.find(ax, m.a) === root));
}

export type ApplyResult = DistanceResult & { solidAdded?: number };

/**
 * The sheets' Apply: `trySetDistance`, and with `useNew` (after a conflict)
 * first clear that axis group. On ok, `solidAdded` is how many walls turned
 * solid. With `useNew` the clear happens only if the apply then succeeds.
 */
export function applyDistance(
  plan: Plan, r: Room, ax: Axis, p: number, q: number, val: number, moveQ: boolean,
  kind: Measurement['kind'], opts: { useNew?: boolean; chosen?: boolean } = {},
): ApplyResult {
  const before = knownCount(r);
  const pick = { chosen: opts.chosen };
  if (opts.useNew) {
    const saved = r.measurements;
    clearAxisGroup(r, ax, p);
    const res = trySetDistance(plan, r, ax, p, q, val, moveQ, kind, pick);
    if (!res.ok) r.measurements = saved;
    return res.ok ? { ...res, solidAdded: knownCount(r) - before } : res;
  }
  const res = trySetDistance(plan, r, ax, p, q, val, moveQ, kind, pick);
  return res.ok ? { ...res, solidAdded: knownCount(r) - before } : res;
}

export { findOpposite };

/** Which action a wall's sheet leads with (F4): rough walls lead with length. */
export function primaryWallAction(r: Room, vid: number): 'len' | 'across' {
  const E = edgeOf(r, vid);
  if (!E) return 'len';
  return edgeKnown(buildUF(r), E.a, E.b) ? 'across' : 'len';
}

export interface AcrossInfo {
  ax: Axis;
  /** The wall you stand at and the one across. */
  from: number;
  to: number;
  currentFt: number;
  /** The across distance is already fixed by earlier measurements (F4). */
  alreadySet: boolean;
  /** This wall is solid; if not, offer "Measure its length". */
  wallKnown: boolean;
  /** Default: move the far wall, unless it is held by a shared wall (F5); on an added room, J18. */
  moveFar: boolean;
  /** FIELD-3 J18: the default comes from the wall the room was attached by; the screen asks nothing first. */
  fromAttached: boolean;
}

export function acrossInfo(plan: Plan, rid: string, vid: number): AcrossInfo | Fail<'no-opposite' | 'missing'> {
  const r = findRoom(plan, rid);
  const E = r && edgeOf(r, vid);
  if (!r || !E) return { ok: false, err: 'missing', message: 'That wall is gone' };
  const opp = findOpposite(r, vid);
  if (!opp) return { ok: false, err: 'no-opposite', message: 'No wall faces this one across the room' };
  const ax: Axis = axisOf(E.a, E.b) === 'h' ? 'y' : 'x';
  const U = buildUF(r);
  const side = attachedSide(r, ax, E.a.id, opp.a.id);
  return {
    ax, from: E.a.id, to: opp.a.id, currentFt: Math.abs(opp.a[ax] - E.a[ax]),
    alreadySet: U.same(ax, E.a.id, opp.a.id), wallKnown: edgeKnown(U, E.a, E.b),
    moveFar: side ?? !isPinned(plan, r, U, ax, opp.a.id), fromAttached: side != null,
  };
}

/**
 * FIELD-3 J18: on a room added by Add room beyond, drag-out or Add closet, the
 * wall to move between corners `near` and `far` (axis `ax`) is the one on the far
 * side from the wall it was attached by: true = move `far`, false = move `near`,
 * null when neither is on the attached wall's line (the F5 default applies).
 */
function attachedSide(r: Room, ax: Axis, near: number, far: number): boolean | null {
  const id = r.attachedEdge;
  const E = id != null ? edgeOf(r, id) : null;
  if (!E || (axisOf(E.a, E.b) === 'h' ? 'y' : 'x') !== ax) return null;
  const U = buildUF(r, false);
  if (U.same(ax, near, E.a.id)) return true;
  if (U.same(ax, far, E.a.id)) return false;
  return null;
}

/**
 * FIELD-3 J18 for the screen: which end a measurement on wall `vid` of an added
 * room moves by default, and whether that came from the attached wall (no swap
 * prompt first). `across`: true = the far wall; `len`: true = the wall's end.
 * Null when the wall is gone or has no opposite.
 */
export function defaultMoveSide(
  plan: Plan, rid: string, vid: number, kind: Measurement['kind'],
): { moveOther: boolean; fromAttached: boolean } | null {
  const info = kind === 'across' ? acrossInfo(plan, rid, vid) : lengthInfo(plan, rid, vid);
  if ('ok' in info) return null;
  return { moveOther: 'moveFar' in info ? info.moveFar : info.moveEnd, fromAttached: info.fromAttached };
}

/** Measure across from wall `vid` (F4). `moveFar` defaults per F5. */
export function measureAcross(
  plan: Plan, rid: string, vid: number, valueFt: number, opts: { moveFar?: boolean; useNew?: boolean; chosen?: boolean } = {},
): ApplyResult | Fail<'no-opposite' | 'missing' | 'too-short'> {
  const info = acrossInfo(plan, rid, vid);
  if ('ok' in info) return info;
  if (valueFt < POLICY.across_min_ft) return { ok: false, err: 'too-short', message: 'Too short. Check the reading.' };
  const r = findRoom(plan, rid)!;
  return applyDistance(plan, r, info.ax, info.from, info.to, valueFt, opts.moveFar ?? info.moveFar, 'across', { useNew: opts.useNew, chosen: opts.chosen });
}

export interface LengthInfo { ax: Axis; a: number; b: number; currentFt: number; moveEnd: boolean; fromAttached: boolean }

export function lengthInfo(plan: Plan, rid: string, vid: number): LengthInfo | Fail<'missing' | 'not-straight'> {
  const r = findRoom(plan, rid);
  const E = r && edgeOf(r, vid);
  if (!r || !E) return { ok: false, err: 'missing', message: 'That wall is gone' };
  const t = axisOf(E.a, E.b);
  if (t !== 'h' && t !== 'v') return { ok: false, err: 'not-straight', message: 'Only straight walls' };
  const ax: Axis = t === 'h' ? 'x' : 'y';
  const side = attachedSide(r, ax, E.a.id, E.b.id);
  return {
    ax, a: E.a.id, b: E.b.id, currentFt: dist(E.a, E.b),
    moveEnd: side ?? !isPinned(plan, r, buildUF(r), ax, E.b.id), fromAttached: side != null,
  };
}

/** Set a wall's own length. `moveEnd` (the blue end) defaults per F5. */
export function wallLength(
  plan: Plan, rid: string, vid: number, valueFt: number, opts: { moveEnd?: boolean; useNew?: boolean; chosen?: boolean } = {},
): ApplyResult | Fail<'missing' | 'not-straight' | 'too-short'> {
  const info = lengthInfo(plan, rid, vid);
  if ('ok' in info) return info;
  if (valueFt < POLICY.length_min_ft) return { ok: false, err: 'too-short', message: 'Too short. Check the reading.' };
  const r = findRoom(plan, rid)!;
  return applyDistance(plan, r, info.ax, info.a, info.b, valueFt, opts.moveEnd ?? info.moveEnd, 'len', { useNew: opts.useNew, chosen: opts.chosen });
}
